// @ts-nocheck
// Clerk-API-shaped compatibility layer over the Mongo-backed AuthProvider.
// The 86 files that imported `@clerk/clerk-react` are codemodded to import from
// here instead, so they keep working unchanged. Auth actually flows via the
// httpOnly cookie; `getToken()` is a no-op (returns null).
import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Settings, LogOut, Bug } from 'lucide-react';
import { openFeedbackDialog } from '@/components/FeedbackDialog/FeedbackDialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { AuthProvider, useAdminAuth } from './AuthProvider';

const orgToClerk = (o) => (o ? { id: String(o._id || o), name: o.displayName || o.name, slug: o.name } : null);
const adminToUser = (a) => (a ? {
  id: String(a._id),
  primaryEmailAddress: { emailAddress: a.email },
  emailAddresses: [{ emailAddress: a.email, id: String(a._id) }],
  firstName: a.firstName,
  lastName: a.lastName,
  fullName: [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email,
  publicMetadata: {},
  organizationMemberships: ((a.organizations) || []).map((o) => ({
    id: String(o._id || o),
    organization: orgToClerk(o),
    role: (a.provider && a.provider.role) || 'org:member',
  })),
  // Clerk's user object exposed this as a method; the org switcher/nav call it.
  getOrganizationMemberships: async () => ((a.organizations) || []).map((o) => ({
    id: String(o._id || o),
    organization: orgToClerk(o),
    role: (a.provider && a.provider.role) || 'org:member',
  })),
} : null);

// Stable no-op token getter (cookie carries auth) — reused so refs don't change.
const noopGetToken = async () => null;

export function useAuth() {
  const { admin, activeOrg, isLoaded, isSignedIn, logout } = useAdminAuth();
  return React.useMemo(() => ({
    isLoaded,
    isSignedIn,
    userId: admin ? String(admin._id) : null,
    orgId: activeOrg ? String(activeOrg._id) : null,
    orgSlug: activeOrg ? activeOrg.name : null,
    getToken: noopGetToken,
    signOut: logout,
  }), [admin, activeOrg, isLoaded, isSignedIn, logout]);
}

export function useUser() {
  const { admin, isLoaded } = useAdminAuth();
  const user = React.useMemo(() => adminToUser(admin), [admin]);
  return React.useMemo(() => ({ isLoaded, isSignedIn: !!admin, user }), [isLoaded, admin, user]);
}

export function useOrganization() {
  const { activeOrg, isLoaded } = useAdminAuth();
  const organization = React.useMemo(() => orgToClerk(activeOrg), [activeOrg]);
  return React.useMemo(() => ({ isLoaded, organization }), [isLoaded, organization]);
}

export function useOrganizationList() {
  const { organizations, isLoaded, switchOrg } = useAdminAuth();
  return {
    isLoaded,
    setActive: async ({ organization } = {}) => switchOrg(organization),
    userMemberships: { data: (organizations || []).map((o) => ({ organization: orgToClerk(o) })) },
  };
}

export function useClerk() {
  const { logout } = useAdminAuth();
  return { signOut: logout, openUserProfile: () => {} };
}

export function SignedIn({ children }) {
  const { isLoaded, isSignedIn } = useAdminAuth();
  if (!isLoaded) return null;
  return isSignedIn ? <>{children}</> : null;
}

export function SignedOut({ children }) {
  const { isLoaded, isSignedIn } = useAdminAuth();
  if (!isLoaded) return null;
  return !isSignedIn ? <>{children}</> : null;
}

export function RedirectToSignIn() {
  return <Navigate to="/sign-in" replace />;
}

// Proper avatar menu (replaces the old underlined "Sign out" text link):
// initials avatar → dropdown with identity, Settings, Sign out. Extra props
// (Clerk's afterSignOutUrl/appearance) are accepted and ignored. Rendered only
// inside the router (OrganizationLayout), so Link is safe here.
export function UserButton(_props) {
  const { admin, logout } = useAdminAuth();

  const name = [admin?.firstName, admin?.lastName].filter(Boolean).join(' ') || admin?.email || 'Admin';
  const initials = (admin?.firstName || admin?.email || 'A').charAt(0).toUpperCase()
    + (admin?.lastName ? admin.lastName.charAt(0).toUpperCase() : '');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground ring-offset-background transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Account menu"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm font-medium text-foreground">{name}</div>
          {admin?.email ? (
            <div className="truncate text-xs text-muted-foreground">{admin.email}</div>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/admin/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openFeedbackDialog} className="cursor-pointer">
          <Bug className="mr-2 h-4 w-4" />
          Report an issue
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function OrganizationSwitcher() {
  const { organizations, activeOrg, switchOrg } = useAdminAuth();
  return (
    <select
      className="border rounded px-2 py-1 text-sm"
      value={activeOrg ? String(activeOrg._id) : ''}
      onChange={(e) => switchOrg(e.target.value).then(() => window.location.reload())}
    >
      {(organizations || []).map((o) => (
        <option key={String(o._id)} value={String(o._id)}>{o.displayName || o.name}</option>
      ))}
    </select>
  );
}

// ClerkProvider → AuthProvider (props ignored). Lets Root.tsx keep its JSX.
export function ClerkProvider({ children }) {
  return <AuthProvider>{children}</AuthProvider>;
}

// Auth-flow hooks/components are only referenced by the auth/SSO pages we rewrite
// — stub them so any stragglers don't crash the bundle.
export function useSignIn() { return { isLoaded: true, signIn: null, setActive: async () => {} }; }
export function useSignUp() { return { isLoaded: true, signUp: null, setActive: async () => {} }; }
export function AuthenticateWithRedirectCallback() { return null; }

export { AuthProvider, useAdminAuth };
